<script setup>
import { computed } from 'vue'
import { Badge, Button, Table } from '@drama/ui'
import { useI18n } from 'vue-i18n'

import ModelPricing from '../ModelPricing.vue'

const props = defineProps({
  capability: { type: String, required: true },
  catalog: { type: Object, required: true },
  models: { type: Array, required: true },
  saving: { type: Boolean, required: true },
})
const emit = defineEmits(['edit', 'set-route', 'set-enabled'])
const { t } = useI18n()

const currentCapability = computed(() => props.catalog[props.capability])
const providersById = computed(() => new Map(props.catalog.providers.map(row => [row.id, row])))
const columns = computed(() => {
  const tableColumns = [
    { key: 'model', label: t('visualGameAdmin.llm.capabilities.table.model'), width: '310px', mono: true },
    { key: 'provider', label: t('visualGameAdmin.llm.capabilities.table.provider'), width: '130px' },
    ...(props.capability === 'embedding'
      ? [{ key: 'dimensions', label: t('visualGameAdmin.llm.capabilities.table.dimensions'), width: '90px' }]
      : []),
    ...pricingColumns(props.capability),
    { key: 'enabled', label: t('visualGameAdmin.llm.capabilities.table.status'), width: '120px' },
  ]
  if (props.capability === 'image' || props.capability === 'video' || props.capability === 'embedding') {
    tableColumns.push({
      key: 'route',
      label: t('visualGameAdmin.llm.capabilities.table.productionModel'),
      width: '120px',
    })
  }
  tableColumns.push({
    key: 'actions',
    label: t('visualGameAdmin.common.actions'),
    width: '220px',
    align: 'right',
  })
  return tableColumns
})

function pricingColumns(capability) {
  switch (capability) {
    case 'text':
      return [
        { key: 'input_range', label: t('visualGameAdmin.llm.capabilities.pricing.ranges.title'), width: '120px' },
        { key: 'input_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.input'), width: '180px' },
        { key: 'output_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.output'), width: '180px' },
        { key: 'cached_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.cached'), width: '180px' },
        { key: 'cache_write_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.cacheWrite'), width: '180px' },
      ]
    case 'image':
      return [{ key: 'image_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.image'), width: '140px' }]
    case 'video':
      return [
        { key: 'video_with_input_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.withVideo'), width: '190px' },
        { key: 'video_without_input_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.withoutVideo'), width: '210px' },
      ]
    case 'tts':
      return [{ key: 'tts_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.tts'), width: '160px' }]
    case 'embedding':
      // 向量只有输入，价目就是 token_tiered 的单档输入价，复用 input_price 这一列。
      return [{ key: 'input_price', label: t('visualGameAdmin.llm.capabilities.pricing.headers.input'), width: '180px' }]
    default:
      throw new Error(`Unhandled model capability: ${capability}`)
  }
}

function providerCapabilityForModel(model) {
  return currentCapability.value.provider_capabilities.find(row => row.id === model.provider_capability_id)
}

function providerForModel(model) {
  return providersById.value.get(providerCapabilityForModel(model).provider_id)
}

function isCurrentRoute(model) {
  return currentCapability.value.route_model_id === model.id
}
</script>

<template>
  <Table :columns="columns" :rows="models">
    <template #provider="{ row }">
      <div class="capability-model-table__identity">
        <span>{{ providerForModel(row).display_name }}</span>
        <code>{{ providerCapabilityForModel(row).code }}</code>
      </div>
    </template>
    <template #dimensions="{ row }">{{ row.dimensions }}</template>
    <template #input_range="{ row }"><ModelPricing :pricing="row.pricing" column="input_range" /></template>
    <template #input_price="{ row }"><ModelPricing :pricing="row.pricing" column="input_price" /></template>
    <template #output_price="{ row }"><ModelPricing :pricing="row.pricing" column="output_price" /></template>
    <template #cached_price="{ row }"><ModelPricing :pricing="row.pricing" column="cached_price" /></template>
    <template #cache_write_price="{ row }"><ModelPricing :pricing="row.pricing" column="cache_write_price" /></template>
    <template #image_price="{ row }"><ModelPricing :pricing="row.pricing" column="image_price" /></template>
    <template #video_with_input_price="{ row }"><ModelPricing :pricing="row.pricing" column="video_with_input_price" /></template>
    <template #video_without_input_price="{ row }"><ModelPricing :pricing="row.pricing" column="video_without_input_price" /></template>
    <template #tts_price="{ row }"><ModelPricing :pricing="row.pricing" column="tts_price" /></template>
    <template #enabled="{ row }">
      <Badge :variant="row.enabled ? 'success' : 'neutral'">
        {{ t(`visualGameAdmin.llm.capabilities.status.${row.enabled ? 'enabled' : 'disabled'}`) }}
      </Badge>
    </template>
    <template #route="{ row }">
      <Badge :variant="isCurrentRoute(row) ? 'info' : 'neutral'">
        {{ t(`visualGameAdmin.llm.capabilities.status.${isCurrentRoute(row) ? 'current' : 'standby'}`) }}
      </Badge>
    </template>
    <template #actions="{ row }">
      <div class="capability-model-table__actions">
        <Button variant="ghost" size="sm" @click="emit('edit', row)">
          {{ t('visualGameAdmin.llm.capabilities.edit') }}
        </Button>
        <Button
          v-if="capability === 'image' || capability === 'video' || capability === 'embedding'"
          v-show="row.enabled && !isCurrentRoute(row)"
          variant="secondary"
          size="sm"
          :loading="saving"
          @click="emit('set-route', row)"
        >
          {{ t('visualGameAdmin.llm.capabilities.setCurrent') }}
        </Button>
        <Button
          v-if="capability === 'tts'"
          variant="secondary"
          size="sm"
          :loading="saving"
          @click="emit('set-enabled', row, !row.enabled)"
        >
          {{ t(`visualGameAdmin.llm.capabilities.${row.enabled ? 'disable' : 'enable'}`) }}
        </Button>
      </div>
    </template>
  </Table>
</template>

<style scoped lang="scss">
.capability-model-table {
  &__identity {
    display: grid;
    gap: var(--drama-space-xs);

    code {
      color: var(--drama-color-text-muted);
      font-size: var(--drama-font-size-xs);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--drama-space-xs);
  }
}
</style>
